import { useState } from 'react'
import { AlertTriangle, Check, Loader2, MapPin, Route, Upload, X } from 'lucide-react'

import { useRoadbook } from '../../data/roadbookContext'
import { anzahl, clock, duration, num } from '../../lib/format'
import { kurzerOrt } from '../../lib/geocode'
import type { DiaryTrack, LatLon } from '../../lib/types'
import { geocodeErrorText } from '../stages/geocodeApi'
import {
  anschlussAbstaende,
  baueEtappen,
  benoetigteOrte,
  FEHLSTUECK_AB_M,
  leseAufzeichnungen,
  ortsSchluessel,
  type Ortsverzeichnis,
  type Tagesentwurf,
} from './anlegen'
import type { Lesefehler } from './mehrdateien'
import { useReverseLookup } from './ortApi'

/**
 * Aus einer Aufzeichnung Etappen anlegen.
 *
 * Der Anlass: gefahren und aufgezeichnet, aber nichts geplant. Bis hierher
 * konnte die App damit nichts anfangen - TrackVerteilen daneben verteilt eine
 * mehrtägige Datei auf **bestehende** Etappen, und wo keine sind, gibt es
 * nichts zu verteilen. Die Regeln, nach denen aus den Fahrtagen Etappen werden,
 * stehen in anlegen.ts; hier steht die Bedienung.
 *
 * Drei Zusagen macht diese Ansicht, und sie sind der ganze Unterschied zu einem
 * Knopf, der eine Datei einliest:
 *
 * Erstens wird nichts überschrieben. Die Etappen kommen ans Ende der Reise,
 * bestehende werden nicht angefasst, nicht umsortiert und nicht ergänzt; die
 * Tagebucheinträge gehören zu Etappen, die es vorher nicht gab, und können
 * deshalb keinen fremden Eintrag treffen. Das Startdatum der Reise wird nur
 * gesetzt, wenn keines dasteht.
 *
 * Zweitens sieht der Nutzer vorher, was entsteht: jeder Tag mit seinem Datum,
 * seinen Uhrzeiten, seinen Kilometern, seinen Halten und dem, was an ihm
 * unsicher ist. Jeder lässt sich abwählen.
 *
 * Drittens wird kein Ort erfunden. Solange niemand Namen geholt hat, heissen
 * Start und Ziel nach ihrer Koordinate - das sieht aus, wie es gemeint ist:
 * nach etwas, das noch niemand benannt hat.
 */
export function EtappenAusAufzeichnung() {
  const { trip, setDiary, setMeta, appendStages } = useRoadbook()
  const nachschlagen = useReverseLookup()

  const [laedt, setLaedt] = useState(false)
  /** Was beim Holen der Ortsnamen schiefgegangen ist - nicht beim Lesen. */
  const [fehler, setFehler] = useState<string | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [warnungen, setWarnungen] = useState<string[]>([])
  /** Die Dateien, aus denen nichts geworden ist, einzeln und mit Grund. */
  const [lesefehler, setLesefehler] = useState<Lesefehler[]>([])
  const [entwuerfe, setEntwuerfe] = useState<Tagesentwurf[]>([])
  const [dateien, setDateien] = useState<string[]>([])
  /** Unbrauchbare Punkte über alle gelesenen Dateien. */
  const [dropped, setDropped] = useState(0)
  const [namen, setNamen] = useState<Ortsverzeichnis>({})
  /** Wie viele Orte geholt sind, während es läuft. */
  const [orte, setOrte] = useState<{ fertig: number; gesamt: number } | null>(null)
  const [mitFehlstuecken, setMitFehlstuecken] = useState(true)

  // Die letzte Etappe der Reise - einschliesslich der ausgelassenen, denn aus
  // der Reihenfolge fällt eine ausgelassene Etappe nicht heraus (siehe
  // startHerkunft in lib/trip.ts). Ihr Ziel ist der Start der ersten neuen.
  const letzte = trip.stages[trip.stages.length - 1]
  const start: LatLon | undefined = letzte ? [letzte.c2[0], letzte.c2[1]] : undefined

  const gewaehlt = entwuerfe.filter((e) => e.aktiv)
  const offen = benoetigteOrte(entwuerfe, start, mitFehlstuecken).filter(
    (at) => !(ortsSchluessel(at) in namen),
  )
  const abstaende = anschlussAbstaende(entwuerfe, start)

  function zuruecksetzen() {
    setEntwuerfe([])
    setDateien([])
    setNamen({})
    setWarnungen([])
    setLesefehler([])
    setDropped(0)
    setOrte(null)
  }

  /**
   * Liest die gewählten Dateien ein.
   *
   * Gelesen und zu Fahrtagen gelegt wird in anlegen.ts (leseAufzeichnungen);
   * hier steht nur, wohin das Ergebnis geht. Eine Datei, aus der nichts wird,
   * kostet nur sich selbst - sie steht mit ihrem Namen unter dem Knopf, und die
   * übrigen stehen als Fahrtage darunter. Diese Maske trifft es am härtesten:
   * hier liegen die meisten Dateien auf einmal im Dialog, weil aus ihnen eine
   * ganze Reise entsteht.
   */
  async function nimmDateien(liste: FileList | null) {
    if (!liste || liste.length === 0) return
    setLaedt(true)
    setFehler(null)
    setMeldung(null)
    zuruecksetzen()

    const lese = await leseAufzeichnungen(liste, Date.now())
    setEntwuerfe(lese.entwuerfe)
    setDateien(lese.dateien)
    setWarnungen(lese.warnungen)
    setLesefehler(lese.fehler)
    setDropped(lese.dropped)
    setLaedt(false)
  }

  /**
   * Holt die Namen der Punkte, die einen brauchen.
   *
   * Einer nach dem anderen und nicht alle auf einmal: der Dienst erlaubt eine
   * Anfrage je Sekunde, und der Server drosselt entsprechend. Parallel
   * abgeschickt stünden sie dort in einer Schlange, von der die hinteren
   * abgewiesen würden - siehe ErrThrottled in internal/geocoding.
   *
   * Ein Fehlschlag hält den Rest nicht auf. Ein Punkt ohne Namen behält seine
   * Koordinate, und das ist eine Etappe, die man von Hand beschriften kann;
   * ein Abbruch nach dem dritten von siebzehn wäre eine, die man von vorn
   * anfangen muss.
   */
  async function holeOrte() {
    const punkte = benoetigteOrte(entwuerfe, start, mitFehlstuecken).filter(
      (at) => !(ortsSchluessel(at) in namen),
    )
    if (punkte.length === 0) return

    setFehler(null)
    setOrte({ fertig: 0, gesamt: punkte.length })
    let letzterFehler: unknown = null

    for (const [i, at] of punkte.entries()) {
      try {
        const treffer = await nachschlagen(at)
        if (treffer) {
          const eintrag = { name: kurzerOrt(treffer.name), land: treffer.country }
          setNamen((alt) => ({ ...alt, [ortsSchluessel(at)]: eintrag }))
        }
      } catch (err) {
        letzterFehler = err
      }
      setOrte({ fertig: i + 1, gesamt: punkte.length })
    }

    setOrte(null)
    if (letzterFehler !== null) {
      setFehler(
        `${geocodeErrorText(letzterFehler)} Die Punkte ohne Namen behalten ihre Koordinate; ` +
          'noch einmal drücken holt nur die fehlenden.',
      )
    }
  }

  function anlegen() {
    const { stages, eintraege } = baueEtappen(entwuerfe, start, namen, mitFehlstuecken)
    if (stages.length === 0) return

    // Das Startdatum der Reise nur, wenn keines dasteht. Es verschiebt den
    // Kalender jeder bestehenden Etappe (travelDayDate in lib/calendar.ts) -
    // ein gesetztes zu überschreiben, hiesse die Planung anderer Tage
    // umzudatieren, und das hat hier niemand verlangt.
    const ersterTag = gewaehlt.find((e) => e.datum)?.datum
    if (trip.meta.start === '' && ersterTag) setMeta({ start: ersterTag })

    appendStages(stages)
    for (const eintrag of eintraege) setDiary(eintrag.stageId, eintrag.patch)

    setMeldung(
      `${anzahl(stages.length, 'Etappe', 'Etappen')} angelegt, jede mit ihrer Aufzeichnung, ` +
        'ihrem Datum und ihren gefahrenen Kilometern. Namen, Zeiten und alles Übrige lassen ' +
        'sich in der Etappenliste ändern.',
    )
    zuruecksetzen()
  }

  return (
    <section className="strecken kein-druck">
      <h4 className="block-titel">
        <Route size={13} /> Etappen aus einer Aufzeichnung
      </h4>

      <p className="strecken-text">
        Gefahren, aufgezeichnet, aber nichts geplant? Aus den GPX-Dateien entsteht je Fahrtag eine
        Etappe — mit Start und Ziel aus der Aufzeichnung, ihrem Datum, ihren Kilometern und dem
        Track im Tagebuch. Mehrere Dateien eines Tages gehören zusammen und ergeben eine Etappe.
        Angelegt wird nichts, bevor du die Liste gesehen hast.
      </p>

      <div className="knopfreihe">
        <label className="knopf still">
          {laedt ? <Loader2 size={14} className="dreht" /> : <Upload size={14} />}
          {laedt ? 'Dateien werden gelesen …' : 'GPX-Dateien wählen'}
          <input
            type="file"
            accept=".gpx,application/gpx+xml,application/xml,text/xml"
            multiple
            hidden
            onChange={(e) => {
              void nimmDateien(e.target.files)
              e.target.value = ''
            }}
          />
        </label>
        {(entwuerfe.length > 0 || lesefehler.length > 0) && (
          <button type="button" className="knopf still" onClick={zuruecksetzen}>
            <X size={14} /> Abbrechen
          </button>
        )}
      </div>

      {/*
        Die Dateien, aus denen nichts geworden ist, stehen einzeln da und mit
        Namen. Eine Sammelmeldung wäre hier wertlos: wer fünf Dateien gewählt
        hat, muss wissen, welche der fünf gemeint ist - und dass die übrigen als
        Fahrtage darunter stehen. Der Schlüssel ist die Stelle in der Liste,
        denn zweimal dieselbe Datei ergibt zweimal denselben Satz.
      */}
      {lesefehler.map((f, i) => (
        <p className="karte-warnung" key={i}>
          <AlertTriangle size={13} /> {f.name}: {f.grund}
        </p>
      ))}

      {entwuerfe.length > 0 && (
        <>
          <p className="strecken-text">
            {dateien.join(', ')} · {anzahl(entwuerfe.length, 'Fahrtag', 'Fahrtage')}
            {dropped > 0 && ` · ${num(dropped)} unbrauchbare Punkte übersprungen`}
          </p>

          {warnungen.map((warnung, i) => (
            <p className="karte-warnung" key={i}>
              <AlertTriangle size={13} /> {warnung}
            </p>
          ))}

          {letzte && (
            <p className="strecken-text">
              Diese Reise hat schon {anzahl(trip.stages.length, 'Etappe', 'Etappen')}. Die neuen
              kommen dahinter, und die erste von ihnen beginnt am Ziel der letzten bestehenden (
              {letzte.to || 'ohne Namen'}) — das Ziel einer Etappe ist der Start der nächsten.
              Bestehende Etappen werden nicht verändert.
            </p>
          )}

          <ol className="eintragsliste">
            {entwuerfe.map((entwurf, i) => (
              <li className="eintrag" key={entwurf.datum || `ohne-datum-${String(i)}`}>
                <span className="eintrag-nummer">
                  {entwurf.datum || 'ohne Datum'} · {tagZeile(entwurf.track)}
                </span>

                <div className="eintrag-zeile">
                  <span className="strecken-text">
                    {benennungText(entwurf.von, namen)} → {benennungText(entwurf.bis, namen)}
                  </span>
                  <label className="knopf still">
                    <input
                      type="checkbox"
                      checked={entwurf.aktiv}
                      onChange={(e) => {
                        const aktiv = e.target.checked
                        setEntwuerfe((alt) => alt.map((x, k) => (k === i ? { ...x, aktiv } : x)))
                      }}
                    />
                    Anlegen
                  </label>
                </div>

                {abstaende[i] !== undefined && (
                  <span className="karte-warnung">
                    <AlertTriangle size={13} /> Diese Etappe beginnt {abstandText(abstaende[i])} von
                    der Stelle entfernt, an der die Aufzeichnung anfängt — dazwischen liegt Weg, den
                    keine Datei zeigt. Sie beginnt trotzdem dort, wo die vorige endet: das ist die
                    Kette, die die Reise zusammenhält. Verschieben lässt sich der Punkt danach in
                    der Etappenliste.
                  </span>
                )}

                {entwurf.track.parts && entwurf.track.parts.length > 1 && (
                  <span className="strecken-text">
                    {anzahl(entwurf.track.parts.length, 'Aufzeichnung', 'Aufzeichnungen')} an diesem
                    Tag, nach der Uhr zusammengelegt.
                    {entwurf.doppelt > 0 &&
                      ` ${anzahl(entwurf.doppelt, 'Stück lag', 'Stücke lagen')} schon vor und zählen nicht zweimal.`}
                  </span>
                )}

                {entwurf.fehlstuecke.map((luecke) => (
                  <span className="karte-warnung" key={luecke.ab}>
                    <AlertTriangle size={13} /> Zwischen {clock(luecke.ab)} und {clock(luecke.bis)}
                    {' fehlen '}
                    {abstandText(luecke.meter)} Aufzeichnung — dort ist gefahren worden, ohne dass
                    das Gerät mitgeschrieben hat. Die Luftlinie steht in den Kilometern; gefahren
                    war es mehr.
                  </span>
                ))}

                {entwurf.halte.length > 0 && (
                  <ol className="track-stopps">
                    {entwurf.halte.map((halt) => (
                      <li key={halt.from}>
                        <b>{duration((halt.to - halt.from) / 1000)}</b>
                        <span>
                          {clock(halt.from)}–{clock(halt.to)}
                        </span>
                        <span className="track-ort">
                          {halt.at[0].toFixed(4)}, {halt.at[1].toFixed(4)}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </li>
            ))}
          </ol>

          {entwuerfe.some((e) => e.fehlstuecke.length > 0) && (
            <>
              <div className="knopfreihe">
                <label className="knopf still">
                  <input
                    type="checkbox"
                    checked={mitFehlstuecken}
                    onChange={(e) => {
                      setMitFehlstuecken(e.target.checked)
                    }}
                  />
                  Ränder der Fehlstücke als Zwischenziele
                </label>
              </div>
              <p className="strecken-text">
                Wo die Aufzeichnung abreisst, zeichnet die Karte eine Gerade, die niemand so
                gefahren ist. Als Zwischenziele lässt sich für die Etappe später eine Strecke
                berechnen, die dort entlangführt, wo eine Strasse ist — angezeigt wird sie neben der
                aufgezeichneten und als das, was sie ist: die geplante Linie.
              </p>
            </>
          )}

          <div className="knopfreihe">
            <button
              type="button"
              className="knopf still"
              disabled={orte !== null || offen.length === 0}
              onClick={() => {
                void holeOrte()
              }}
            >
              {orte ? <Loader2 size={14} className="dreht" /> : <MapPin size={14} />}
              {orte
                ? `Orte werden geholt … ${String(orte.fertig)} von ${String(orte.gesamt)}`
                : offen.length === 0
                  ? 'Orte sind geholt'
                  : `Orte holen (${anzahl(offen.length, 'Anfrage', 'Anfragen')}, etwa ${anzahl(offen.length, 'Sekunde', 'Sekunden')})`}
            </button>

            <button
              type="button"
              className="knopf"
              disabled={gewaehlt.length === 0 || orte !== null}
              onClick={anlegen}
            >
              <Check size={14} />
              {gewaehlt.length === 0
                ? 'Nichts ausgewählt'
                : `${anzahl(gewaehlt.length, 'Etappe', 'Etappen')} anlegen`}
            </button>
          </div>
        </>
      )}

      {meldung && <p className="strecken-text">{meldung}</p>}
      {fehler && <p className="karte-warnung">{fehler}</p>}

      <p className="abschluss">
        Eine Etappe ist ein Tag — deshalb entsteht je Kalendertag eine, auch wenn das Gerät den Tag
        in mehrere Dateien geschnitten hat. Start und Ziel kommen aus der Aufzeichnung, und das Ziel
        des einen Tages ist der Start des nächsten; die Kette der Reise bleibt damit geschlossen.
        Eine Datei, aus der nichts wird, kostet nur sich selbst: sie steht mit ihrem Namen und ihrem
        Grund unter dem Knopf, und die übrigen ergeben trotzdem ihre Fahrtage. Ortsnamen holt erst
        der Knopf oben, und zwar einen je Etappengrenze und nicht einen je Trackpunkt: der Dienst
        erlaubt eine Anfrage je Sekunde. Benannt wird auf der Höhe von Dorf und Stadtteil — die
        ersten Fixes einer Aufzeichnung liegen um einige zehn Meter daneben, und eine Hausnummer
        wäre damit genau falsch. Aus demselben Grund gilt ein Sprung zwischen zwei Aufzeichnungen
        von weniger als {num(FEHLSTUECK_AB_M)} Metern als derselbe Ort und wird nicht erwähnt: der
        Wagen steht am Morgen ein paar Wagenlängen anders auf dem Hof als am Abend.
      </p>
    </section>
  )
}

/** Ein Abstand in Metern oder Kilometern, je nachdem, was sich lesen lässt. */
function abstandText(meter: number): string {
  return meter >= 1000 ? `${num(meter / 1000)} km` : `${num(meter)} m`
}

/** Wie ein Punkt in der Liste dasteht: sein Name, sonst seine Koordinate. */
function benennungText(at: LatLon, namen: Ortsverzeichnis): string {
  const key = ortsSchluessel(at)
  return namen[key]?.name ?? key
}

/** Die Kennzahlen eines Fahrtags in einer Zeile. */
function tagZeile(track: DiaryTrack): string {
  const zeit =
    track.startedAt !== null && track.endedAt !== null
      ? `${clock(track.startedAt)}–${clock(track.endedAt)}`
      : 'ohne Zeitstempel'
  const fahrt = track.movingSeconds > 0 ? `, ${duration(track.movingSeconds)} Fahrzeit` : ''
  // Die Lücke steht nur da, wo es eine gibt - und dann muss sie dastehen: aus
  // der Fahrzeit dieser Zeile entstehen die geplanten Stunden der Etappe, und
  // ein Tag mit zwei Stunden ohne Aufzeichnung ist eine andere Auskunft als
  // einer ohne.
  const luecke =
    track.unrecordedSeconds && track.unrecordedSeconds > 0
      ? `, ${duration(track.unrecordedSeconds)} nicht aufgezeichnet`
      : ''
  return `${zeit} · ${num(track.distanceKm)} km${fahrt}${luecke}`
}
